import User from "./User";
import LeadStatus from "../classes/LeadStatus";
import DBObject from "../utils/DBObject";
import CloneContext from "../utils/CloneContext";

export default class LeadsAssignedListRequest extends DBObject {
  private static readonly _APPLYSTATUS: number = 0;
  private static readonly _ASCENDING: number = 1;
  private static readonly _OFFSET: number = 2;
  private static readonly _ORDERBY: number = 3;
  private static readonly _PAGESIZE: number = 4;
  private static readonly _SEARCHVALUE: number = 5;
  private static readonly _STATUS: number = 6;
  private static readonly _USER: number = 7;
  public id: number = 0;
  public otherMaster: DBObject;
  private _status: LeadStatus = LeadStatus.New;
  private _applyStatus: boolean = false;
  private _user: User = null;
  private _pageSize: number = 0;
  private _offset: number = 0;
  private _orderBy: string = "";
  private _ascending: boolean = false;
  private _searchValue: string = "";
  public constructor(
    d3eParams?: Partial<{
      applyStatus: boolean;
      ascending: boolean;
      offset: number;
      orderBy: string;
      pageSize: number;
      searchValue: string;
      status: LeadStatus;
      user: User;
    }>
  ) {
    super();

    this.setApplyStatus(d3eParams?.applyStatus ?? false);

    this.setAscending(d3eParams?.ascending ?? false);

    this.setOffset(d3eParams?.offset ?? 0);

    this.setOrderBy(d3eParams?.orderBy ?? "");

    this.setPageSize(d3eParams?.pageSize ?? 0);

    this.setSearchValue(d3eParams?.searchValue ?? "");

    this.setStatus(d3eParams?.status ?? LeadStatus.New);

    this.setUser(d3eParams?.user ?? null);
  }
  public get d3eType(): string {
    return "LeadsAssignedListRequest";
  }
  public clear(): void {
    this.d3eChanges.clear();
  }
  public get isTransientModel(): boolean {
    return;
  }
  public get status(): LeadStatus {
    return this._status;
  }
  public setStatus(val: LeadStatus): void {
    let isValChanged: boolean = this._status !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(LeadsAssignedListRequest._STATUS, this._status.index);

    this._status = val;

    this.fire("status", this);
  }
  public get applyStatus(): boolean {
    return this._applyStatus;
  }
  public setApplyStatus(val: boolean): void {
    let isValChanged: boolean = this._applyStatus !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(
      LeadsAssignedListRequest._APPLYSTATUS,
      this._applyStatus
    );

    this._applyStatus = val;

    this.fire("applyStatus", this);
  }
  public get user(): User {
    return this._user;
  }
  public setUser(val: User): void {
    let isValChanged: boolean = this._user !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(LeadsAssignedListRequest._USER, this._user);

    this.updateObservable("user", this._user, val);

    this._user = val;

    this.fire("user", this);
  }
  public get pageSize(): number {
    return this._pageSize;
  }
  public setPageSize(val: number): void {
    let isValChanged: boolean = this._pageSize !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(LeadsAssignedListRequest._PAGESIZE, this._pageSize);

    this._pageSize = val;

    this.fire("pageSize", this);
  }
  public get offset(): number {
    return this._offset;
  }
  public setOffset(val: number): void {
    let isValChanged: boolean = this._offset !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(LeadsAssignedListRequest._OFFSET, this._offset);

    this._offset = val;

    this.fire("offset", this);
  }
  public get orderBy(): string {
    return this._orderBy;
  }
  public setOrderBy(val: string): void {
    let isValChanged: boolean = this._orderBy !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(LeadsAssignedListRequest._ORDERBY, this._orderBy);

    this._orderBy = val;

    this.fire("orderBy", this);
  }
  public get ascending(): boolean {
    return this._ascending;
  }
  public setAscending(val: boolean): void {
    let isValChanged: boolean = this._ascending !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(LeadsAssignedListRequest._ASCENDING, this._ascending);

    this._ascending = val;

    this.fire("ascending", this);
  }
  public get searchValue(): string {
    return this._searchValue;
  }
  public setSearchValue(val: string): void {
    let isValChanged: boolean = this._searchValue !== val;

    if (!isValChanged) {
      return;
    }

    this.updateD3EChanges(
      LeadsAssignedListRequest._SEARCHVALUE,
      this._searchValue
    );

    this._searchValue = val;

    this.fire("searchValue", this);
  }
  public get(field: number): any {
    switch (field) {
      case LeadsAssignedListRequest._STATUS: {
        return this._status.index;
      }

      case LeadsAssignedListRequest._APPLYSTATUS: {
        return this._applyStatus;
      }

      case LeadsAssignedListRequest._USER: {
        return this._user;
      }

      case LeadsAssignedListRequest._PAGESIZE: {
        return this._pageSize;
      }

      case LeadsAssignedListRequest._OFFSET: {
        return this._offset;
      }

      case LeadsAssignedListRequest._ORDERBY: {
        return this._orderBy;
      }

      case LeadsAssignedListRequest._ASCENDING: {
        return this._ascending;
      }

      case LeadsAssignedListRequest._SEARCHVALUE: {
        return this._searchValue;
      }
      default: {
        return null;
      }
    }
  }
  public updateD3EChanges(index: number, value: any): void {
    if (this.lockedChanges()) {
      return;
    }

    super.updateD3EChanges(index, value);
  }
  public restore(): void {
    /*
TODO: Might be removed
*/

    this.d3eChanges.restore(this);
  }
  public deepClone(clearId = true): LeadsAssignedListRequest {
    let ctx: CloneContext = new CloneContext({ "clearId": clearId });

    return ctx.startClone(this);
  }
  public collectChildValues(ctx: CloneContext): void {}
  public deepCloneIntoObj(dbObj: DBObject, ctx: CloneContext): void {
    let obj: LeadsAssignedListRequest = dbObj as LeadsAssignedListRequest;

    obj.id = this.id;

    obj.setStatus(this._status);

    obj.setApplyStatus(this._applyStatus);

    obj.setUser(this._user);

    obj.setPageSize(this._pageSize);

    obj.setOffset(this._offset);

    obj.setOrderBy(this._orderBy);

    obj.setAscending(this._ascending);

    obj.setSearchValue(this._searchValue);
  }
  public set(field: number, value: any): void {
    switch (field) {
      case LeadsAssignedListRequest._STATUS: {
        this.setStatus(LeadStatus.values[value as number]);
        break;
      }

      case LeadsAssignedListRequest._APPLYSTATUS: {
        this.setApplyStatus(value as boolean);
        break;
      }

      case LeadsAssignedListRequest._USER: {
        this.setUser(value as User);
        break;
      }

      case LeadsAssignedListRequest._PAGESIZE: {
        this.setPageSize(value as number);
        break;
      }

      case LeadsAssignedListRequest._OFFSET: {
        this.setOffset(value as number);
        break;
      }

      case LeadsAssignedListRequest._ORDERBY: {
        this.setOrderBy(value as string);
        break;
      }

      case LeadsAssignedListRequest._ASCENDING: {
        this.setAscending(value as boolean);
        break;
      }

      case LeadsAssignedListRequest._SEARCHVALUE: {
        this.setSearchValue(value as string);
        break;
      }
    }
  }
}
